import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type DetailRowTone = "default" | "muted" | "inverse";

export const DETAIL_SECTION_TITLE_CLASS =
  "text-xs font-medium text-surface-500";

const LABEL_CLASS: Record<DetailRowTone, string> = {
  default: DETAIL_SECTION_TITLE_CLASS,
  muted: "text-sm text-surface-600",
  inverse: "text-xs text-primary-800/80",
};

const VALUE_CLASS: Record<DetailRowTone, string> = {
  default: "text-sm text-surface-900",
  muted: "text-sm font-semibold text-surface-900",
  inverse: "text-primary-950",
};

export interface DefinitionListProps {
  className?: string;
  children: ReactNode;
}

export function DefinitionList({ className, children }: DefinitionListProps) {
  return <dl className={cn(className)}>{children}</dl>;
}

export interface DetailSectionTitleProps {
  children: ReactNode;
  className?: string;
  as?: "h2" | "h3" | "p";
}

/** Subsection heading inside detail panels (matches default DetailRow label typography). */
export function DetailSectionTitle({
  children,
  className,
  as: Component = "p",
}: DetailSectionTitleProps) {
  return (
    <Component className={cn(DETAIL_SECTION_TITLE_CLASS, className)}>
      {children}
    </Component>
  );
}

export interface DetailRowProps {
  label: string;
  children: ReactNode;
  icon?: LucideIcon;
  tone?: DetailRowTone;
  /** `inline` lays out label and value on one row (default for `muted`). */
  layout?: "stacked" | "inline";
  className?: string;
  boxed?: boolean;
  valueClassName?: string;
  /** Shown beside the label on stacked rows (e.g. edit action). */
  labelAction?: ReactNode;
}

export function DetailRow({
  label,
  children,
  icon: Icon,
  tone = "default",
  layout,
  className,
  boxed = false,
  valueClassName,
  labelAction,
}: DetailRowProps) {
  const resolvedLayout = layout ?? (tone === "muted" ? "inline" : "stacked");
  const labelClass = LABEL_CLASS[tone];
  const valueClass = cn(VALUE_CLASS[tone], valueClassName);

  if (Icon) {
    return (
      <div
        className={cn(
          "flex gap-3",
          !boxed && "py-2",
          boxed && "min-w-0 rounded-lg bg-surface-50 px-3 py-2.5",
          className,
        )}
      >
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-surface-400" aria-hidden />
        <div className="min-w-0 flex-1">
          <dt className={labelClass}>{label}</dt>
          <dd className={cn("mt-0.5 min-w-0 text-sm text-surface-900", valueClassName)}>
            {children}
          </dd>
        </div>
      </div>
    );
  }

  if (resolvedLayout === "inline") {
    return (
      <div
        className={cn("flex items-center justify-between gap-3", className)}
      >
        <dt className={labelClass}>{label}</dt>
        <dd className={valueClass}>{children}</dd>
      </div>
    );
  }

  const stackedValueClass = cn(
    tone !== "muted" && "mt-0.5",
    tone === "default" && "min-w-0",
    valueClass,
  );

  return (
    <div
      className={cn(
        boxed && "min-w-0 rounded-lg bg-surface-50 px-3 py-2.5",
        className,
      )}
    >
      {labelAction ? (
        <div className="flex items-start justify-between gap-3">
          <dt className={labelClass}>{label}</dt>
          {labelAction}
        </div>
      ) : (
        <dt className={labelClass}>{label}</dt>
      )}
      <dd className={stackedValueClass}>{children}</dd>
    </div>
  );
}
